import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Table, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, UncontrolledTooltip } from "reactstrap";
import instance from "../../axiosInstance";
import useError from "../../hooks/useError";
import CustomDate from "../UI/forms/CustomDate";
import { toYYYYMMDD } from "../../utils/forms";
import { useProfileContext } from "../../context/profile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faChartLine, faChartPie, faMotorcycle, faCar, faBriefcaseMedical, faBoxOpen, faCarSide, faUsers, faChevronLeft, faChevronRight, faExclamationTriangle, faUserTie, faHandshake, faClipboardList, faBullseye, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Chart from 'react-apexcharts';

const MetricCard = ({ title, value, icon, onClick, iconColor, isClickable = true, isLoading = false }) => {
  return (
    <Card 
      className={`shadow-sm ${isClickable && !isLoading ? 'cursor-pointer' : ''} m-1 p-0`}
      style={{ 
        border: 'none',
        borderLeft: `3px solid ${iconColor}`,
        borderRadius: '3px', 
        transition: 'all 0.2s ease-in-out',
        minHeight: '75px',
        marginBottom: '0px !important',
        paddingBottom: '0px !important',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#fff'
      }}
      onClick={isClickable && !isLoading ? onClick : undefined}
      onMouseEnter={(e) => {
        if (!isClickable || isLoading) return;
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 0.25rem 0.75rem rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        if (!isClickable || isLoading) return;
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
      }}
    >
      <div 
        style={{
          position: 'absolute',
          right: '5px',
          bottom: '-10px',
          opacity: 0.08,
          color: iconColor,
          zIndex: 0,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)'
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: '4rem' }} />
      </div>

      <CardBody className="px-3 py-2 d-flex flex-column justify-content-center h-100" style={{ zIndex: 1, position: 'relative' }}>
        <div className="font-weight-bold text-uppercase mb-1 text-truncate" style={{ fontSize: '10.5px', letterSpacing: '0.3px', width: '100%', color: '#858796', textAlign: 'left' }} title={typeof title === 'string' ? title : ''}>
          {title}
        </div>
        <div className="font-weight-bolder" style={{ fontSize: '20px', color: '#153d77', lineHeight: '1', textAlign: 'left' }}>
          {isLoading ? <Spinner size="sm" color="primary" /> : (value || 0)}
        </div>
      </CardBody>
    </Card>
  );
};

const MetricProgressBar = ({ value = 0, target }) => {
  const count = Number(value) || 0;
  const targetNum = Number(target) || 0;

  if (targetNum <= 0) {
    return (
      <div className="w-100 px-2">
        <div style={{ height: '8px', borderRadius: '4px', backgroundColor: '#eaecf4' }} />
        <div className="text-muted text-center mt-1" style={{ fontSize: '10px' }}>No target set</div>
      </div>
    );
  }

  const percentage = (count / targetNum) * 100;
  const barWidth = Math.min(percentage, 100);
  const barColor =
    percentage >= 100 ? '#1cc88a' :
    percentage >= 75 ? '#4e73df' :
    percentage >= 50 ? '#f6c23e' : '#e74a3b';

  return (
    <div className="w-100 px-2">
      <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '10px', lineHeight: 1.2 }}>
        <span className="text-muted">{count} / {targetNum}</span>
        <span className="font-weight-bold" style={{ color: barColor }}>{percentage.toFixed(1)}%</span>
      </div>
      <div style={{ height: '8px', borderRadius: '4px', backgroundColor: '#eaecf4', overflow: 'hidden' }}>
        <div
          style={{
            width: `${barWidth}%`,
            height: '100%',
            backgroundColor: barColor,
            borderRadius: '4px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

const SalesSection = () => {
  const { handleError } = useError();
  const { profile } = useProfileContext();
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));

  const [salesData, setSalesData] = useState(null);
  const [hireProgress, setHireProgress] = useState([]);
  const [solicitorProgress, setSolicitorProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showGraph, setShowGraph] = useState(true);
  const [graphData, setGraphData] = useState(null);

  // Target Modal State
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targets, setTargets] = useState({ pi_target: 0, sources_sold_target: 0, on_hire_target: 0 });

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [activeMetric, setActiveMetric] = useState('');
  const [activeMetricTitle, setActiveMetricTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchSalesData = async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      const [resSales, resHire, resSol, resGraph] = await Promise.all([
        instance.get("/dashboard/salesSection", { params: { month: selectedMonth } }),
        instance.get("/ReportProgress/HireSupplier", { params: { month: selectedMonth, sort_column: 'total', sort_direction: 'desc' } }),
        instance.get("/ReportProgress/Solicitor", { params: { month: selectedMonth, sort_column: 'total', sort_direction: 'desc' } }),
        instance.get("/dashboard/salesSectionGraphData", { params: { month: selectedMonth } })
      ]);

      const sourcesSold = Number(resSales.data.data?.sources_sold) || 0;
      const onHireCount = Number(resSales.data.data?.on_hire) || 0;

      setSalesData({
        ...resSales.data.data,
        sources_sold: sourcesSold,
        on_hire: onHireCount
      });
      setHireProgress(resHire.data.data || []);
      setSolicitorProgress(resSol.data.data || []);
      setGraphData(resGraph.data.data || null);
    } catch (error) {
      handleError(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTargetData = async () => {
    try {
      setTargetLoading(true);
      const res = await instance.get("/dashboard/getSalesTarget", { params: { month: selectedMonth } });
      setTargets({
        total_cases_target: res.data.data.total_cases_target || 0,
        pi_target: res.data.data.pi_target || 0,
        sources_sold_target: res.data.data.sources_sold_target || 0,
        on_hire_target: res.data.data.on_hire_target || 0
      });
    } catch (error) {
      handleError(error);
    } finally {
      setTargetLoading(false);
    }
  };

  const handleOpenTargetModal = () => {
    setTargetModalOpen(true);
    fetchTargetData();
  };

  const handleSaveTargets = async () => {
    try {
      setTargetSaving(true);
      await instance.post("/dashboard/saveSalesTarget", { ...targets, month: selectedMonth });
      setTargetModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setTargetSaving(false);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchSalesData();
      fetchTargetData();
    }
  }, [selectedMonth]);

  const fetchModalData = async (metricKey, page = 1) => {
    try {
      setModalLoading(true);
      setModalError(false);
      const res = await instance.get("/dashboard/salesSectionRecords", {
        params: { metric: metricKey, month: selectedMonth, page, limit: 15 }
      });
      
      setModalData(res.data.data.records || []);
      setTotalPages(res.data.data.total_pages || 1);
      setTotalRecords(res.data.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      handleError(error);
      setModalError(true);
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  const openModal = (metricKey, title) => {
    setActiveMetric(metricKey);
    setActiveMetricTitle(title);
    setModalOpen(true);
    setModalData([]);
    setTotalRecords(0);
    setTotalPages(1);
    fetchModalData(metricKey, 1);
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const getServiceTypes = (item) => {
    const services = [];
    if (item.service_type_personal_injury == 1) services.push('PI');
    if (item.service_type_hire == 1) services.push('Hire');
    if (item.service_type_repair == 1) services.push('Repair');
    if (item.service_type_storage == 1) services.push('Storage');
    if (item.service_type_recovery == 1) services.push('Recovery');
    if (item.service_type_inspection == 1) services.push('Inspection');
    if (item.service_type_outlay == 1) services.push('Outlay');
    return services.length > 0 ? services.join(', ') : (item.case_type || 'N/A');
  };

  const sourceMetrics = ['sources_sold', 'agent', 'two_wheel_garage', 'four_wheel_garage'];
  const isSourceMetric = (metricKey) => sourceMetrics.includes(metricKey);

  const salesMetrics = [
    { key: 'total_cases', title: 'Total Cases', value: salesData?.total_cases, modalTitle: 'Total Cases', isSub: false, targetKey: 'total_cases_target', isSource: false },
    { key: 'pi', title: 'PI', value: salesData?.pi, modalTitle: 'Personal Injury Cases', isSub: false, targetKey: 'pi_target', isSource: false },
    { key: 'packs', title: 'Packs', value: salesData?.packs, modalTitle: 'Pack Cases', isSub: false, isSource: false },
    { key: 'sources_sold', title: 'Sources Sold', value: salesData?.sources_sold, modalTitle: 'Sources Sold', isSub: false, targetKey: 'sources_sold_target', isSource: true },
    { key: 'agent', title: 'Agent (Data Entry)', value: salesData?.agent, modalTitle: 'Agent (Data Entry) Sources', isSub: false, isSource: true },
    { key: 'two_wheel_garage', title: '2 Wheel Garage', value: salesData?.two_wheel_garage, modalTitle: '2 Wheel Garage Sources', isSub: false, isSource: true },
    { key: 'four_wheel_garage', title: '4 Wheel Garage', value: salesData?.four_wheel_garage, modalTitle: '4 Wheel Garage Sources', isSub: false, isSource: true },
    { key: 'total_hire', title: 'Total Hire', value: salesData?.total_hire, modalTitle: 'Total Hire Cases', isSub: false, isSource: false },
    { key: 'on_hire', title: 'On-Hire', value: salesData?.on_hire, modalTitle: 'On-Hire Cases', isSub: false, targetKey: 'on_hire_target', isSource: false },
    { key: 'two_wheel_hire', title: '2 Wheel Hire', value: salesData?.two_wheel_hire, modalTitle: '2 Wheel Hire Cases', isSub: true, isSource: false },
    { key: 'four_wheel_hire', title: '4 Wheel Hire', value: salesData?.four_wheel_hire, modalTitle: '4 Wheel Hire Cases', isSub: true, isSource: false },
  ];

  return (
    <div className="w-100 mb-4 mt-3">
      <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#f8f9fc' }}>
        <CardBody className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-1 p-0 mx-0" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fc', paddingTop: '10px', paddingBottom: '10px', marginTop: '-10px' }}>
            <h4 className="fs-18 mb-0 mx-0 d-flex align-items-center font-weight-bold" style={{ color: '#153d77' }}>
              <span className="text-white p-2 rounded mr-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px', background: '#4e73df' }}>
                <FontAwesomeIcon icon={faChartLine} /> 
              </span>
              Sales
            </h4>
            <div className="d-flex align-items-center bg-white shadow-sm px-3 py-2 rounded border">
              <div className="mr-3 d-flex align-items-center">
                <span className="mr-2 font-weight-bold text-muted fs-12 text-uppercase">Month</span>
                <div style={{ width: '150px' }}>
                  <input 
                    type="month" 
                    className="form-control form-control-sm border-0 bg-light" 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(e.target.value)} 
                    style={{ borderRadius: '8px' }}
                  />
                </div>
              </div>
              <div className="ml-4 pl-3 border-left d-flex align-items-center">
                <Button 
                  color={showGraph ? "primary" : "outline-primary"} 
                  size="sm" 
                  onClick={() => setShowGraph(!showGraph)}
                  className="d-flex align-items-center px-3 py-1 mr-2"
                  style={{ borderRadius: '20px', fontWeight: '500', transition: 'all 0.2s ease' }}
                  title="Toggle graph visibility"
                >
                  <FontAwesomeIcon icon={faChartBar} className="mr-2" />
                  {showGraph ? "Hide Graph" : "Show Graph"}
                </Button>
                {profile?.permission?.dashboard?.set_target && (
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={handleOpenTargetModal}
                    className="d-flex align-items-center px-3 py-1"
                    style={{ borderRadius: '20px', fontWeight: '500', transition: 'all 0.2s ease' }}
                    title="Set Target"
                  >
                    Set Target
                  </Button>
                )}
              </div>
            </div>
          </div>

          {hasError && !isLoading ? (
            <div className="text-center py-5 my-5 text-danger bg-white rounded border">
              <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="mb-3" />
              <h5>Failed to load data</h5>
              <Button color="primary" outline size="sm" onClick={fetchSalesData} className="mt-2">Try Again</Button>
            </div>
          ) : (
            <>
              {isLoading ? (
                <div className="text-center py-5 my-5"><Spinner color="primary" /></div>
              ) : (
                <div className="w-100 mt-4">
                  {showGraph && (
                    <div className="chart-container position-relative mb-4 animate__animated animate__fadeIn">
                      {graphData && (
                        <Chart
                          options={{
                            chart: {
                              type: 'line',
                              height: 280,
                              background: 'transparent',
                              fontFamily: 'inherit',
                              toolbar: { show: false },
                              animations: {
                                enabled: true,
                                easing: 'easeinout',
                                speed: 800
                              }
                            },
                            colors: ['#e74a3b', '#4e73df', '#1cc88a', '#f6c23e'],
                            stroke: {
                              curve: 'smooth',
                              width: 3
                            },
                            grid: {
                              borderColor: '#f1f1f1',
                              strokeDashArray: 4,
                              padding: { top: 0, right: 0, bottom: 0, left: 10 }
                            },
                            dataLabels: {
                              enabled: false
                            },
                            markers: {
                              size: 4,
                              hover: {
                                sizeOffset: 3
                              }
                            },
                            xaxis: {
                              categories: graphData.categories || [],
                              labels: {
                                style: {
                                  fontSize: '11px',
                                  colors: '#858796'
                                }
                              }
                            },
                            yaxis: {
                              labels: {
                                style: {
                                  colors: '#858796'
                                }
                              }
                            },
                            legend: {
                              position: 'top',
                              horizontalAlign: 'right'
                            }
                          }}
                          series={graphData.series || []}
                          type="line"
                          height={280}
                        />
                      )}
                    </div>
                  )}
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0" style={{ borderTop: '1px solid #e3e6f0' }}>
                      <thead>
                        <tr>
                          <th className="text-muted small text-uppercase fw-bold border-top-0 py-3" style={{ fontSize: '11px', width: '32%' }}>Sales Metric</th>
                          <th className="text-center text-muted small text-uppercase fw-bold border-top-0 py-3" style={{ fontSize: '11px', width: '43%' }}>Progress</th>
                          <th className="text-right text-muted small text-uppercase fw-bold border-top-0 py-3" style={{ fontSize: '11px', width: '25%' }}>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesMetrics.map((m, index) => (
                          <tr key={index} className="cursor-pointer" onClick={() => openModal(m.key, m.modalTitle)} style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <td className="fw-medium text-dark py-2 border-bottom align-middle" style={{ fontSize: '13px', paddingLeft: m.isSub ? '2rem' : '0.75rem' }}>
                              {m.isSub && <span className="text-muted mr-2">↳</span>}
                              <span id={`metric-title-${m.key}`}>
                                {m.title}
                              </span>
                              {m.key === 'agent' && (
                                <UncontrolledTooltip placement="right" target={`metric-title-${m.key}`} style={{ fontSize: '12px' }}>
                                  Includes all sources except 2-Wheel and 4-Wheel garages.
                                </UncontrolledTooltip>
                              )}
                            </td>
                            <td className="py-2 border-bottom align-middle" style={{ minWidth: '180px' }}>
                              <MetricProgressBar value={m.value} target={m.targetKey ? targets[m.targetKey] : 0} />
                            </td>
                            <td className="text-right fw-bold py-2 border-bottom align-middle" style={{ fontSize: '13px', color: '#20c997' }}>
                              {m.targetKey && targets[m.targetKey] > 0 ? (
                                <div className="d-flex flex-column align-items-end">
                                  <span>
                                    {m.value || 0}{' '}
                                    <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>/ {targets[m.targetKey]}</span>
                                  </span>
                                  <span className="text-primary mt-1" style={{ fontSize: '11px' }}>
                                    {(((m.value || 0) / targets[m.targetKey]) * 100).toFixed(1)}%
                                  </span>
                                </div>
                              ) : (
                                m.value || 0
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <Row className="mt-2 m-0 w-100 p-0">
                <Col lg="6" className="p-1">
                  <div className="bg-white shadow-sm border rounded overflow-hidden h-100">
                    <div className="bg-light px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                      <div className="font-weight-bold fs-13 text-uppercase" style={{ letterSpacing: '0.5px', color: '#153d77' }}>CHO Progress (Hire Suppliers)</div>
                      <span className="badge px-3 py-1 rounded-pill" style={{ backgroundColor: '#4e73df', color: 'white', fontSize: '11px', letterSpacing: '0.5px' }}>
                        TOTAL: {hireProgress?.reduce((sum, item) => sum + (parseInt(item.total) || 0), 0) || 0}
                      </span>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto", position: 'relative' }}>
                      <Table size="sm" hover className="mb-0 border-0" style={{ fontSize: '13px' }}>
                        <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                          <tr>
                            <th className="pl-4 border-top-0 border-bottom py-2 text-muted">Sr.</th>
                            <th className="border-top-0 border-bottom py-2 text-muted">Hire Supplier</th>
                            <th className="text-center pr-4 border-top-0 border-bottom py-2 text-muted">Total Cases</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr><td colSpan="3" className="text-center py-5"><Spinner color="primary" /></td></tr>
                          ) : hireProgress?.length > 0 ? (
                            hireProgress.map((item, index) => (
                              <tr key={index}>
                                <td className="pl-4 align-middle text-muted">{index + 1}</td>
                                <td className="align-middle font-weight-bold" style={{ color: '#153d77' }}>{item.full_name}</td>
                                <td className="text-center pr-4 align-middle">
                                  <div className="d-flex justify-content-center align-items-center">
                                    <span className="badge px-3 py-1 rounded-pill" style={{ backgroundColor: '#4e73df', color: 'white', minWidth: '35px' }}>{item.total || "0"}</span>
                                    {item.on_hire_count > 0 && (
                                      <span className="badge px-2 py-1 rounded-pill ml-2 shadow-sm" style={{ backgroundColor: '#20c997', color: 'white', fontSize: '11px' }} title="On-Hire Cases">
                                        {item.on_hire_count} OH
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center text-muted py-5">
                                <div className="mb-2"><FontAwesomeIcon icon={faBoxOpen} size="2x" className="text-light" /></div>
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
                
                <Col lg="6" className="p-1">
                  <div className="bg-white shadow-sm border rounded overflow-hidden h-100">
                    <div className="bg-light px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                      <div className="font-weight-bold fs-13 text-uppercase" style={{ letterSpacing: '0.5px', color: '#153d77' }}>Solicitor Progress</div>
                      <span className="badge px-3 py-1 rounded-pill" style={{ backgroundColor: '#4e73df', color: 'white', fontSize: '11px', letterSpacing: '0.5px' }}>
                        TOTAL: {solicitorProgress?.reduce((sum, item) => sum + (parseInt(item.total) || 0), 0) || 0}
                      </span>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto", position: 'relative' }}>
                      <Table size="sm" hover className="mb-0 border-0" style={{ fontSize: '13px' }}>
                        <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                          <tr>
                            <th className="pl-4 border-top-0 border-bottom py-2 text-muted">Sr.</th>
                            <th className="border-top-0 border-bottom py-2 text-muted">Solicitor</th>
                            <th className="text-center pr-4 border-top-0 border-bottom py-2 text-muted">Total Cases</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr><td colSpan="3" className="text-center py-5"><Spinner color="primary" /></td></tr>
                          ) : solicitorProgress?.length > 0 ? (
                            solicitorProgress.map((item, index) => (
                              <tr key={index}>
                                <td className="pl-4 align-middle text-muted">{index + 1}</td>
                                <td className="align-middle font-weight-bold" style={{ color: '#153d77' }}>{item.full_name}</td>
                                <td className="text-center pr-4 align-middle">
                                  <span className="badge px-3 py-1 rounded-pill" style={{ backgroundColor: '#4e73df', color: 'white' }}>{item.total || "-"}</span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center text-muted py-5">
                                <div className="mb-2"><FontAwesomeIcon icon={faBoxOpen} size="2x" className="text-light" /></div>
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" centered fade>
        <ModalHeader toggle={toggleModal} className="bg-light border-bottom">
          <span className="font-weight-bold fs-16" style={{ color: '#153d77' }}>{activeMetricTitle}</span>
          {!modalLoading && !modalError && (
            <span className="ml-3 badge py-1 px-2" style={{ backgroundColor: '#4e73df', color: 'white' }}>{totalRecords} Records</span>
          )}
        </ModalHeader>
        <ModalBody className="p-0 bg-white">
          {modalLoading ? (
            <div className="text-center py-5 my-5"><Spinner color="primary" /></div>
          ) : modalError ? (
            <div className="text-center py-5 my-5 text-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
              <p>Failed to load records.</p>
              <Button color="primary" outline size="sm" onClick={() => fetchModalData(activeMetric, currentPage)}>Try Again</Button>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '60vh' }}>
              <Table striped hover size="sm" className="mb-0" style={{ fontSize: '14px' }}>
                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th className="pl-4 py-3 border-top-0">#</th>
                    {isSourceMetric(activeMetric) ? (
                      <>
                        <th className="py-3 border-top-0">Supplier Name</th>
                        <th className="py-3 border-top-0">Last Case Provided</th>
                      </>
                    ) : (
                      <>
                        <th className="py-3 border-top-0">Case ID</th>
                        <th className="py-3 border-top-0">Service Type</th>
                        <th className="py-3 border-top-0">Created At</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {modalData.length > 0 ? (
                    modalData.map((item, idx) => (
                      <tr key={idx}>
                        <td className="pl-4 align-middle text-muted">{(currentPage - 1) * 15 + idx + 1}</td>
                        {isSourceMetric(activeMetric) ? (
                          <>
                            <td className="align-middle font-weight-bold" style={{ color: '#153d77' }}>{item.supplier_name || 'N/A'}</td>
                            <td className="align-middle">{item.created_at ? moment(item.created_at).format('DD MMM YYYY, HH:mm') : '-'}</td>
                          </>
                        ) : (
                          <>
                            <td className="align-middle font-weight-bold">
                              <a href={`/administration/cases/${item.case_id}/edit`} target="_blank" rel="noreferrer" className="text-decoration-none" style={{ color: '#4e73df' }}>
                                {item.case_id}
                              </a>
                            </td>
                            <td className="align-middle">{getServiceTypes(item)}</td>
                            <td className="align-middle">{item.created_at ? moment(item.created_at).format('DD MMM YYYY, HH:mm') : '-'}</td>
                          </>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-5 text-muted">
                        <div className="mb-2"><FontAwesomeIcon icon={faBoxOpen} size="2x" className="text-light" /></div>
                        No records found for this metric in the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light border-top d-flex justify-content-between align-items-center py-3">
          <div>
            {!modalLoading && !modalError && (
              <span className="text-muted font-weight-bold fs-13">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
          <div>
            <Button 
              color="primary" 
              outline 
              size="sm" 
              className="mr-2 px-3 font-weight-bold" 
              onClick={() => fetchModalData(activeMetric, currentPage - 1)}
              disabled={currentPage <= 1 || modalLoading || modalError}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="mr-2" /> Prev
            </Button>
            <Button 
              color="primary" 
              outline 
              size="sm"
              className="px-3 font-weight-bold"
              onClick={() => fetchModalData(activeMetric, currentPage + 1)}
              disabled={currentPage >= totalPages || modalLoading || modalError}
            >
              Next <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Target Modal */}
      <Modal isOpen={targetModalOpen} toggle={() => setTargetModalOpen(!targetModalOpen)} centered>
        <ModalHeader toggle={() => setTargetModalOpen(!targetModalOpen)}>Set Targets ({moment(selectedMonth).format('MMMM YYYY')})</ModalHeader>
        <ModalBody className="p-4">
          {targetLoading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : (
            <div className="d-flex flex-column gap-3">
              <FormGroup>
                <Label className="font-weight-bold">Total Cases Target</Label>
                <Input 
                  type="number" 
                  value={targets.total_cases_target || ''} 
                  onChange={(e) => setTargets({...targets, total_cases_target: e.target.value})}
                  placeholder="Enter Total Cases target"
                />
              </FormGroup>
              <FormGroup>
                <Label className="font-weight-bold">Personal Injury (PI) Target</Label>
                <Input 
                  type="number" 
                  value={targets.pi_target} 
                  onChange={(e) => setTargets({...targets, pi_target: e.target.value})}
                  placeholder="Enter PI target"
                />
              </FormGroup>
              <FormGroup>
                <Label className="font-weight-bold">Sources Sold Target</Label>
                <Input 
                  type="number" 
                  value={targets.sources_sold_target} 
                  onChange={(e) => setTargets({...targets, sources_sold_target: e.target.value})}
                  placeholder="Enter Sources Sold target"
                />
              </FormGroup>
              <FormGroup>
                <Label className="font-weight-bold">On Hire Target</Label>
                <Input 
                  type="number" 
                  value={targets.on_hire_target} 
                  onChange={(e) => setTargets({...targets, on_hire_target: e.target.value})}
                  placeholder="Enter On Hire target"
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setTargetModalOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleSaveTargets} disabled={targetSaving || targetLoading}>
            {targetSaving && <Spinner size="sm" className="mr-2" />}
            Save Targets
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SalesSection;
